import {
  BillingStrategy,
  PromoCodeDisplayMessageType,
  type PlanDetailVO,
  type PromoCodeVO,
  type Rating,
  type RatingCallRequest,
} from '@ctct/domain-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { PromoCodesPageStore } from '@ctct/state-client-layer';
import type { UsePromoValidation } from '@ctct/state-server-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { promoCodesEventBus } from '../PromoCodesEventBus';
import type { PromoCodesUrlAdapter } from '../types';

export class PromoCodesService {
  constructor(
    private pageStore: PromoCodesPageStore,
    private usePromoValidation: UsePromoValidation,
    private urlAdapter: PromoCodesUrlAdapter | null,
    private ratingRequest: RatingCallRequest | null,
    private rating: Rating | undefined,
    private planDetail: PlanDetailVO | undefined,
    private selectedEmailPlanId: number | null,
    /** `null` = billing strategy unknown; must not validate via BaaS yet. */
    private billingStrategy: BillingStrategy | null,
    private t: TranslationGetter,
    isHidePromoCodesFeatureGateEnabled: boolean,
    private shouldHidePromoCodes: boolean,
    private isBillingStrategyPending: boolean
  ) {
    const { promoCodesToBeValidated, promoRevalidationPlanId } = this.pageStore.getState();

    pageStore.setState({
      promoCodeValidationPending: usePromoValidation.isPending,
      promoCodesIsHiddenFeatureGateEnabled: isHidePromoCodesFeatureGateEnabled,
    });

    // if there are promo codes in store, which are not yet validated (because for example we did not have selectedEmailPlanId before), we apply them now
    // NOTE - if there is validation in progress, we  need to wait for it to finish before applying the codes
    if (this.canApplyQueuedPromoCodes(promoCodesToBeValidated, promoRevalidationPlanId)) {
      this.applyManyPromoCodes(promoCodesToBeValidated);
      // Clear promoCodesToBeValidated after attempting to apply them to prevent re-applying on subsequent renders
      // This prevents flickering errors when invalid promo codes are provided in URL params
      this.pageStore.setState({
        promoCodesToBeValidated: [],
        promoRevalidationPlanId: null,
      });
    }

    // remove promo codes from "to be validated" if they are already validated and we have them in the rating response
    const validPromoCodes = this.rating?.getNewAppliedDiscountCodes();

    if (validPromoCodes?.length) {
      const filteredPromoCodesToBeValidated = promoCodesToBeValidated.filter(
        (code) => !validPromoCodes.some((validCode) => validCode.getName() === code)
      );

      this.pageStore.setState({
        promoCodesToBeValidated:
          filteredPromoCodesToBeValidated.length === promoCodesToBeValidated.length
            ? promoCodesToBeValidated
            : filteredPromoCodesToBeValidated,
      });
    }

    // shouldHidePromoCodes is true when the hide_promo_codes_for_stripe_multimonth
    // feature gate is active AND the user has selected a 6- or 12-month billing period.
    // Stash the codes so they can be restored if the user switches back to monthly.
    if (this.shouldHidePromoCodes && validPromoCodes?.length) {
      this.pageStore.setState({
        stashedMonthlyPromoCodes: validPromoCodes.map((code) => code.getName()),
      });
      this.clearAllPromoCodes(validPromoCodes);
    }

    // Restore stashed promo codes when switching back to monthly
    if (!this.shouldHidePromoCodes && isHidePromoCodesFeatureGateEnabled) {
      const { stashedMonthlyPromoCodes } = this.pageStore.getState();

      if (stashedMonthlyPromoCodes.length) {
        this.pageStore.setState({ stashedMonthlyPromoCodes: [] });

        // Filter out codes already present in the rating to avoid "already applied" errors
        const alreadyApplied = this.getValidatedPromoCodeNames();
        const codesToRestore = stashedMonthlyPromoCodes.filter((code) => !alreadyApplied.includes(code));

        if (codesToRestore.length) {
          this.applyManyPromoCodes(codesToRestore);
        }
      }
    }

    this.syncUnappliedPromoCodesFromRating();
  }

  public api() {
    return {
      getPromoCodesToBeValidated: () => this.pageStore.getState().promoCodesToBeValidated,
      getValidatedPromoCodes: () => this.getValidatedPromoCodeNames(),
      getPromoCodeTextFieldValue: () => this.pageStore.getState().promoCodeTextFieldValue,
      clearPromoCodeTextFieldValue: () => this.pageStore.setState({ promoCodeTextFieldValue: '' }),
      applyPromoCodes: async (codes: string[]) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const code of codes) {
          // eslint-disable-next-line no-await-in-loop
          await this.applyPromoCode(code.toUpperCase(), 'api');
        }
      },
    };
  }

  public data() {
    return {
      getIsLoading: () => this.usePromoValidation.isPending,
      getValidatedPromoCodes: () => {
        const promoCodes = this.getValidatedPromoCodes();

        return promoCodes.map((promoCodeVO) => ({
          name: promoCodeVO.getName(),
          displayValue: this.getDisplayValue(promoCodeVO),
          isProrated: promoCodeVO.isProrated(),
          isLocked: promoCodeVO.isLocked(),
          isNonDiscount: promoCodeVO.isNonDiscount(),
        }));
      },
    };
  }

  public handlers() {
    return {
      setPromoCodeTextFieldValue: (newValue: string) => {
        this.pageStore.setState({ promoCodeTextFieldValue: newValue });
      },
      onRemovePromoCodeClick: (name: string) => this.removePromoCode(name, 'user'),
    };
  }

  public async onMount() {
    if (!this.urlAdapter) return [];

    const { promoCodes } = this.urlAdapter.getPromoCodesParams();

    // if selectedEmailPlanId or planDetail is not available, we store promo codes in the page store until the plan is selected
    if (!this.selectedEmailPlanId || !this.planDetail) {
      this.pageStore.setState({
        promoCodesToBeValidated: promoCodes,
      });
    } else {
      // Only apply codes not already validated (avoids "already applied" error on second onMount)
      const alreadyApplied = this.getValidatedPromoCodeNames();
      const { failedUrlParamCodes } = this.pageStore.getState();

      // Codes may have failed via BaaS while Stripe was still unknown / wrongly in-house.
      // Once we know we should use Stripe (rating) validation, allow URL codes to retry.
      const skipFailedFilter = this.shouldUseStripeValidation();
      const toApply = promoCodes.filter(
        (code) => !alreadyApplied.includes(code) && (skipFailedFilter || !failedUrlParamCodes.includes(code))
      );

      if (toApply.length) {
        await this.applyManyPromoCodes(toApply);
      }
    }

    return promoCodes;
  }

  public getValidatedPromoCodes() {
    return this.rating?.getNewAppliedDiscountCodes() || [];
  }

  public getValidatedPromoCodeNames() {
    return this.getValidatedPromoCodes().map((code) => code.getName());
  }

  /**
   * Promo codes in the rating payload are the source of truth for the next rating/validation call.
   * Returns null when the rating request has not been initialized yet.
   */
  private getAppliedPromotionCodesFromPayload(): string[] | null {
    if (!this.ratingRequest) {
      return null;
    }

    return this.ratingRequest.newPromotionCodes ?? [];
  }

  private queuePromoCodeForValidation(promoCode: string) {
    const { promoCodesToBeValidated } = this.pageStore.getState();

    if (!promoCodesToBeValidated.includes(promoCode)) {
      this.pageStore.setState({
        promoCodesToBeValidated: [...promoCodesToBeValidated, promoCode],
      });
    }
  }

  private canApplyQueuedPromoCodes(promoCodesToBeValidated: string[], promoRevalidationPlanId: number | null) {
    if (!promoCodesToBeValidated.length) {
      return false;
    }

    // Keep codes queued while promo entry is hidden (Stripe 6/12).
    if (this.shouldHidePromoCodes) {
      return false;
    }

    // While billing strategy is pending, we temporarily prevent promo code application.
    // Applying now would hit BaaS /promotions/validation and permanently fail Stripe-only codes.
    if (this.isStripeStatusUnresolved()) {
      return false;
    }

    if (!this.selectedEmailPlanId || !this.planDetail || this.usePromoValidation.isPending || !this.ratingRequest) {
      return false;
    }

    if (promoRevalidationPlanId === null) {
      return true;
    }

    return (
      promoRevalidationPlanId === this.selectedEmailPlanId && this.ratingRequest.newPlanId === this.selectedEmailPlanId
    );
  }

  /**
   * Maps the promo code display message type enum to a translated string.
   * Also formats discount amounts if applicable.
   */
  public getDisplayValue(promoCodeVO: PromoCodeVO): string {
    const displayMessageType = promoCodeVO.getDisplayMessageType();
    const currency = this.rating?.getCurrency();

    if (!displayMessageType) {
      return '';
    }

    if (displayMessageType === PromoCodeDisplayMessageType.GENERIC_DISCOUNT) {
      const discountAmount = promoCodeVO.getDiscountAmount();

      if (!discountAmount || !currency) {
        return '';
      }

      return discountAmount.format(currency);
    }

    if (displayMessageType === PromoCodeDisplayMessageType.GENERIC_NON_DISCOUNT) {
      return this.t('application-layer.promo-codes.display-messages.generic-non-discount');
    }

    return '';
  }

  private clearErrors() {
    this.pageStore.setState({ promoCodeFormErrors: [] });
  }

  private clearTextField() {
    this.pageStore.setState({ promoCodeTextFieldValue: '' });
  }

  public setErrors(errors: string[]) {
    this.pageStore.setState({ promoCodeFormErrors: errors });
  }

  public getPromoCodeTextFieldValue() {
    return this.pageStore.getState().promoCodeTextFieldValue;
  }

  public async applyPromoCodeFromTextField() {
    const { promoCodeTextFieldValue: promoCode } = this.pageStore.getState();

    await this.applyPromoCode(promoCode, 'user');

    this.clearTextField();
  }

  private async applyManyPromoCodes(promoCodes: string[]) {
    // eslint-disable-next-line no-restricted-syntax
    for (const promoCode of promoCodes) {
      // We need to apply promo codes one at a time since that's how the endpoint works.
      // eslint-disable-next-line no-await-in-loop
      await this.applyPromoCode(promoCode, 'onMount');
    }
  }

  public async applyPromoCode(promoCodeInput: string, source: TEventSource) {
    const promoCode = promoCodeInput.toUpperCase();

    // Stripe 6/12 with hide_promo_codes_for_stripe_multimonth: do not apply yet, but queue
    // so codes can run when the period becomes monthly.
    if (this.shouldHidePromoCodes) {
      this.queuePromoCodeForValidation(promoCode);

      return;
    }

    // Do not validate via BaaS while billing strategy is pending.
    if (this.isStripeStatusUnresolved()) {
      this.queuePromoCodeForValidation(promoCode);

      return;
    }

    const appliedPromotionCodes = this.getAppliedPromotionCodesFromPayload();

    // promocode was already applied to the rating payload
    if (appliedPromotionCodes?.includes(promoCode)) {
      const errorMessage = this.t('application-layer.promo-codes.add.error-already-applied');

      this.setErrors([errorMessage]);
      promoCodesEventBus.emit('promoCodeApplyError', promoCode, [errorMessage], { source });

      return;
    }

    if (source !== 'onMount') {
      this.clearErrors();
    }

    if (!this.selectedEmailPlanId || !this.planDetail) {
      this.queuePromoCodeForValidation(promoCode);

      return;
    }

    const validationResult = await this.validatePromoCode(promoCode, this.selectedEmailPlanId, this.planDetail);

    if (!validationResult.success) {
      const errorStrings = validationResult.messages.map((message) =>
        message.promotionCode ? `"${message.promotionCode}": ${message.reasonMessage}` : message.reasonMessage
      );

      this.setErrors(errorStrings);
      promoCodesEventBus.emit('promoCodeApplyError', promoCode, errorStrings, { source });
      observabilityAppService.notify(new Error('Promo code validation failed'), { promoCode, validationResult });

      if (source === 'onMount') {
        const { failedUrlParamCodes } = this.pageStore.getState();

        if (!failedUrlParamCodes.includes(promoCode)) {
          this.pageStore.setState({
            failedUrlParamCodes: [...failedUrlParamCodes, promoCode],
          });
        }
      }

      return;
    }

    promoCodesEventBus.emit('promoCodeApplySuccess', promoCode, { source });
  }

  public removePromoCode(name: string, source: TEventSource) {
    promoCodesEventBus.emit('promoCodeRemove', name, { source });

    this.clearErrors();
  }

  private clearAllPromoCodes(promoCodes: PromoCodeVO[]) {
    promoCodes.forEach((promoCode) => {
      this.removePromoCode(promoCode.getName(), 'api');
    });
  }

  /**
   * When the rating response reports unapplied promo codes that are still in the payload,
   * remove them from subsequent rating calls and register them in failedUrlParamCodes so
   * errors display on load and later valid codes can be applied cleanly.
   */
  private syncUnappliedPromoCodesFromRating() {
    if (!this.rating || !this.ratingRequest) {
      return;
    }

    const payloadCodes = this.getAppliedPromotionCodesFromPayload();

    if (!payloadCodes?.length) {
      return;
    }

    const { failedUrlParamCodes, promoCodeFormErrors } = this.pageStore.getState();
    const codesToRemove: string[] = [];
    const nextFailedUrlParamCodes = [...failedUrlParamCodes];
    const nextPromoCodeFormErrors = [...promoCodeFormErrors];

    this.rating.getUnappliedPromoCodes().forEach((unappliedPromo) => {
      const promotionCode = unappliedPromo.promotionCode?.toUpperCase();

      if (!promotionCode || !payloadCodes.includes(promotionCode)) {
        return;
      }

      const errorString = `"${promotionCode}": ${unappliedPromo.reasonMessage}`;

      if (!nextFailedUrlParamCodes.includes(promotionCode)) {
        nextFailedUrlParamCodes.push(promotionCode);
      }

      if (!nextPromoCodeFormErrors.includes(errorString)) {
        nextPromoCodeFormErrors.push(errorString);
      }

      codesToRemove.push(promotionCode);
    });

    if (!codesToRemove.length) {
      return;
    }

    this.pageStore.setState({
      failedUrlParamCodes: nextFailedUrlParamCodes,
      promoCodeFormErrors: nextPromoCodeFormErrors,
    });

    queueMicrotask(() => {
      codesToRemove.forEach((promotionCode) => {
        promoCodesEventBus.emit('promoCodeRemove', promotionCode, { source: 'api' });
      });
    });
  }

  private isStripeStatusUnresolved(): boolean {
    return this.isBillingStrategyPending || this.billingStrategy == null;
  }

  private shouldUseStripeValidation(): boolean {
    // Prefer explicit Stripe strategy; also honor rating payload if session restore already marked it.
    return this.billingStrategy === BillingStrategy.Stripe || Boolean(this.ratingRequest?.stripeAccount);
  }

  private async validatePromoCode(promoCode: string, selectedEmailPlanId: number, planDetail: PlanDetailVO) {
    const appliedPromotionCodes = this.getAppliedPromotionCodesFromPayload() ?? [];

    if (this.shouldUseStripeValidation()) {
      const { ratingRequest } = this;

      if (!ratingRequest) {
        throw new Error('Rating request is required');
      }

      return this.usePromoValidation.mutateAsync({
        billingStrategy: BillingStrategy.Stripe,
        baasAddonIds: this.rating?.getAddons().map((addon) => addon.id) || [],
        planGroupId: planDetail.getPlanGroupId(),
        planId: selectedEmailPlanId,
        promotionCodes: [...appliedPromotionCodes, promoCode],
        ratingRequest: ratingRequest.withAddedPromotionCode(promoCode),
      });
    }

    return this.usePromoValidation.mutateAsync({
      billingStrategy: BillingStrategy.InHouse,
      baasAddonIds: this.rating?.getAddons().map((addon) => addon.id) || [],
      planGroupId: planDetail.getPlanGroupId(),
      planId: selectedEmailPlanId,
      promotionCodes: [...appliedPromotionCodes, promoCode],
    });
  }
}
